import {
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi, type ConcessionByDiscountType, type ConcessionByFeeType, type WaiverRow } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';
import { useSchoolTheme } from '@/themes/use-theme';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function ConcessionsReportPage() {
  const { token } = antdTheme.useToken();
  const { chartColors } = useSchoolTheme();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'concessions', from, to],
    queryFn: () => reportsApi.concessions(from, to),
  });

  const tooltipStyle = {
    background: token.colorBgElevated,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 8,
  };

  const discTypeChart = (data?.byDiscountType ?? []).map((d) => ({
    name: d.discountTypeName,
    amount: Number(d.amount),
  }));

  const feeTypeChart = (data?.byFeeType ?? []).map((t) => ({
    name: t.feeTypeName ?? '—',
    discount: Number(t.discount),
    waived: Number(t.waived),
  }));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Reports / Finance"
        title="Concessions — discounts & waivers"
        description="How much fee revenue was forgone in the period, by fee type and by discount type. Counted by the invoice's billing period."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/concessions.xlsx',
              params: { from, to },
              fileName: `concessions-${from}-to-${to}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
          picker="date"
        />
      </Space>

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={4}>
              <Card><Statistic title="Gross billed" value={PKR(data.grossBilled)} prefix="₨" /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Discount given" value={PKR(data.totalDiscount)} prefix="₨" styles={{ content: { color: chartColors[0] } }} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Waived" value={PKR(data.totalWaived)} prefix="₨" styles={{ content: { color: token.colorWarning } }} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Total concession" value={PKR(data.totalConcession)} prefix="₨" styles={{ content: { color: token.colorError, fontWeight: 700 } }} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Concession %" value={`${PKR(data.concessionPct)}%`} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card><Statistic title="Net receivable" value={PKR(data.netReceivable)} prefix="₨" styles={{ content: { color: token.colorSuccess } }} /></Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <Card title={<Typography.Text strong>By discount type</Typography.Text>} style={{ height: '100%' }}>
                {discTypeChart.length === 0 ? (
                  <Empty description="No discounts in this range" />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={discTypeChart}
                        dataKey="amount"
                        nameKey="name"
                        innerRadius={64}
                        outerRadius={100}
                        paddingAngle={discTypeChart.length > 1 ? 2 : 0}
                        stroke="none"
                        startAngle={90}
                        endAngle={-270}
                      >
                        {discTypeChart.map((_, i) => (
                          <Cell key={i} fill={chartColors[i % chartColors.length]} />
                        ))}
                        <Label
                          position="center"
                          content={({ viewBox }) => {
                            const vb = viewBox as { cx: number; cy: number };
                            if (!vb) return null;
                            return (
                              <text x={vb.cx} y={vb.cy} textAnchor="middle" dominantBaseline="central">
                                <tspan x={vb.cx} dy="-0.4em" fontSize={12} fill={token.colorTextSecondary}>Total</tspan>
                                <tspan x={vb.cx} dy="1.5em" fontSize={16} fontWeight={700} fill={token.colorText}>
                                  ₨ {PKR(data.totalDiscount)}
                                </tspan>
                              </text>
                            );
                          }}
                        />
                      </Pie>
                      <Legend />
                      <Tooltip formatter={(v: number, n) => [`₨ ${PKR(v)}`, n as string]} contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card title={<Typography.Text strong>Concession by fee type</Typography.Text>} style={{ height: '100%' }}>
                {feeTypeChart.length === 0 ? (
                  <Empty description="No data" />
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, feeTypeChart.length * 46)}>
                    <BarChart data={feeTypeChart} layout="vertical" margin={{ left: 12, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} horizontal={false} />
                      <XAxis type="number" stroke={token.colorTextSecondary} fontSize={12} tickFormatter={(v) => PKR(v as number)} />
                      <YAxis type="category" dataKey="name" stroke={token.colorTextSecondary} fontSize={12} width={120} />
                      <Tooltip formatter={(v: number, n) => [`₨ ${PKR(v)}`, n as string]} contentStyle={tooltipStyle} cursor={{ fill: token.colorFillTertiary }} />
                      <Legend />
                      <Bar dataKey="discount" name="Discount" stackId="c" fill={chartColors[0]} radius={[0, 0, 0, 0]} />
                      <Bar dataKey="waived" name="Waived" stackId="c" fill={chartColors[4 % chartColors.length]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
          </Row>

          <Card title="Breakdown by fee type" styles={{ body: { padding: 0 } }}>
            {data.byFeeType.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No concessions in this range" />
            ) : (
              <Table<ConcessionByFeeType>
                rowKey="feeTypeId"
                size="small"
                pagination={false}
                dataSource={data.byFeeType}
                columns={[
                  { title: 'Fee type', dataIndex: 'feeTypeName', render: (n: string | null) => n ?? '—' },
                  { title: 'Invoices', dataIndex: 'invoiceCount', width: 100, align: 'right' },
                  { title: 'Gross', dataIndex: 'gross', width: 130, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                  { title: 'Discount', dataIndex: 'discount', width: 130, align: 'right', render: (a: string) => <span style={{ color: Number(a) > 0 ? chartColors[0] : undefined }}>₨ {PKR(a)}</span> },
                  { title: 'Waived', dataIndex: 'waived', width: 130, align: 'right', render: (a: string) => <span style={{ color: Number(a) > 0 ? token.colorWarning : undefined }}>₨ {PKR(a)}</span> },
                  { title: 'Concession', dataIndex: 'concession', width: 140, align: 'right', render: (a: string) => <Typography.Text strong>₨ {PKR(a)}</Typography.Text> },
                ]}
              />
            )}
          </Card>

          <Card title="By discount type" styles={{ body: { padding: 0 } }}>
            {data.byDiscountType.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No discounts in this range" />
            ) : (
              <Table<ConcessionByDiscountType>
                rowKey={(r) => String(r.discountTypeId)}
                size="small"
                pagination={false}
                dataSource={data.byDiscountType}
                columns={[
                  { title: 'Discount type', dataIndex: 'discountTypeName' },
                  { title: 'Students', dataIndex: 'studentCount', width: 120, align: 'right' },
                  { title: 'Amount', dataIndex: 'amount', width: 160, align: 'right', render: (a: string) => <Typography.Text strong>₨ {PKR(a)}</Typography.Text> },
                ]}
              />
            )}
          </Card>

          <Card title={`Waivers granted (${data.waivers.length})`} styles={{ body: { padding: 0 } }}>
            {data.waivers.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No waivers in this range" />
            ) : (
              <Table<WaiverRow>
                rowKey="invoiceId"
                size="small"
                loading={isFetching}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                dataSource={data.waivers}
                scroll={{ x: 900 }}
                columns={[
                  { title: 'Reg #', dataIndex: 'registrationNumber', width: 110, render: (n: string | null) => n ?? '—' },
                  { title: 'Student', dataIndex: 'studentName' },
                  { title: 'Class', width: 120, render: (_, r) => (r.className ? `${r.className} · ${r.sectionName}` : '—') },
                  { title: 'Fee type', dataIndex: 'feeTypeName', width: 130, render: (n: string | null) => n ?? '—' },
                  { title: 'Period', dataIndex: 'periodMonth', width: 110, render: (m: string | null) => (m ? dayjs(m).format('MMM YYYY') : '—') },
                  { title: 'Waived', dataIndex: 'amount', width: 120, align: 'right', render: (a: string) => <Typography.Text strong>₨ {PKR(a)}</Typography.Text> },
                  { title: 'Reason', dataIndex: 'reason', render: (r: string | null) => r ?? '—' },
                  { title: 'By', dataIndex: 'approvedBy', width: 110, render: (a: string | null) => a ?? '—' },
                  { title: 'When', dataIndex: 'waivedAt', width: 130, render: (w: string | null) => (w ? dayjs(w).format('D MMM YYYY') : '—') },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
